/** Feeds translated wording to the generic @motolynk/ui controls (searchable selects, date pickers, scroll arrows). */

import { useMemo, type ReactNode } from "react";

import { useTranslation } from "@motolynk/i18n";
import { UiStringsProvider, type UiStrings } from "@motolynk/ui";

/**
 * Wrap an app once. `namespace` holds a `ui` section with the keys of `UiStrings` (panels: "common",
 * storefront: "storefront").
 */
export function TranslatedUiStrings({
  namespace,
  children,
}: {
  namespace: string;
  children: ReactNode;
}) {
  const { t, i18n } = useTranslation(namespace);
  const language = i18n.language;
  const strings = useMemo<Partial<UiStrings>>(
    () => ({
      scrollLeft: t("ui.scrollLeft"),
      scrollRight: t("ui.scrollRight"),
      search: t("ui.search"),
      noResults: t("ui.noResults"),
      clear: t("ui.clear"),
      loading: t("ui.loading"),
      selectPlaceholder: t("ui.selectPlaceholder"),
      pickDate: t("ui.pickDate"),
      pickDateTime: t("ui.pickDateTime"),
      previousMonth: t("ui.previousMonth"),
      nextMonth: t("ui.nextMonth"),
      today: t("ui.today"),
      time: t("ui.time"),
      month: t("ui.month"),
      year: t("ui.year"),
      hours: t("ui.hours"),
      minutes: t("ui.minutes"),
      done: t("ui.done"),
      locale: language,
      fileTooLarge: (name, max) => t("ui.fileTooLarge", { name, max }),
      fileType: (name, types) => t("ui.fileType", { name, types }),
      chooseFiles: t("ui.chooseFiles"),
      fileLimits: (count, types, size) => t("ui.fileLimits", { count, types, size }),
      fileLimitsTotal: (count, types, size) => t("ui.fileLimitsTotal", { count, types, size }),
      filesTooLarge: (size) => t("ui.filesTooLarge", { size }),
      removeFile: (name) => t("ui.removeFile", { name }),
    }),
    // `language` changes whenever `t` would return different text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  );
  return <UiStringsProvider strings={strings}>{children}</UiStringsProvider>;
}
